import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import howItWorks1 from "@/assets/how-it-works-1-snap.jpg";
import howItWorks2 from "@/assets/how-it-works-2-inventory.jpg";
import howItWorks3 from "@/assets/how-it-works-3-find.jpg";

interface StepProps {
  image: string;
  alt: string;
  title: string;
  description: React.ReactNode;
  delay: number;
}

function AnimatedStep({ image, alt, title, description, delay }: StepProps) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`flex flex-col sm:flex-row gap-6 items-start transition-all duration-700 ease-out ${
        isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-full sm:w-48 flex-shrink-0 overflow-hidden rounded-xl">
        <img
          src={image}
          alt={alt}
          className={`w-full h-40 sm:h-36 object-cover transition-transform duration-700 ease-out ${
            isVisible ? "scale-100" : "scale-105"
          }`}
          style={{ transitionDelay: `${delay + 100}ms` }}
        />
      </div>
      <div className="flex-1">
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

export function HowItWorksSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({
    threshold: 0.3,
  });

  const steps = [
    {
      image: howItWorks1,
      alt: "Person taking photo of box contents",
      title: "Snap Before Sealing",
      description: (
        <>
          Take a <span className="text-foreground font-medium">quick photo</span> of
          what's inside each box before you tape it shut. That's all you need to do.
        </>
      ),
    },
    {
      image: howItWorks2,
      alt: "Person viewing inventory on phone",
      title: "Instant Inventory",
      description: (
        <>
          We identify every item and create an{" "}
          <span className="text-foreground font-medium">itemized list</span> for that
          box — automatically.
        </>
      ),
    },
    {
      image: howItWorks3,
      alt: "Person finding items in boxes",
      title: "Find Anything",
      description: (
        <>
          <span className="text-foreground font-medium">Search "coffee maker"</span>{" "}
          and know exactly which box to open. No more guessing on moving day.
        </>
      ),
    },
  ];

  return (
    <div className="bg-muted/30 py-20 lg:py-24">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 lg:gap-16 max-w-6xl mx-auto">
          {/* Left Column - Header */}
          <div
            ref={headerRef}
            className={`lg:sticky lg:top-24 lg:self-start transition-all duration-700 ease-out ${
              headerVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6"
            }`}
          >
            <p className="text-sm text-muted-foreground mb-3 tracking-wide">
              How it works
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-6">
              From chaos to organized.
            </h2>
            <Button variant="outline" className="rounded-full" asChild>
              <Link to="/auth">Learn more</Link>
            </Button>
          </div>

          {/* Right Column - Steps */}
          <div className="space-y-12 lg:space-y-16">
            {steps.map((step, index) => (
              <AnimatedStep
                key={step.title}
                {...step}
                delay={index * 150}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
